import { ProductDetail, ProductListItem } from '../types/ProductTypes';

/**
 * Maps API response to ProductDetail
 * The new API returns camelCase properties
 */
export const mapProductFromApi = (apiProduct: any): ProductDetail => {
    return {
        lastUpdatedUtc: apiProduct.lastUpdatedUtc,
        id: apiProduct.id,
        categoryId: apiProduct.categoryId,
        synonyms: (apiProduct.synonyms || []).map((s: any) => ({
            id: s.id,
            synonymName: s.synonymName,
        })),
        procedures: (apiProduct.procedures || []).map((p: any) => ({
            id: p.id,
            lastUpdatedUtc: p.lastUpdatedUtc,
            title: p.title,
            description: p.description,
            comments: p.comments,
            filePath: p.filePath,
            category: p.category,
            score: p.score,
            displayInformation: p.displayInformation,
        })),
        coatingCompatibilities: (apiProduct.coatingCompatibilities || []).map((c: any) => ({
            id: c.id,
            coating: c.coating,
            compatibility: c.compatibility,
            // API returns MaxTemperature as a Temperature object { value: number } - extract the raw value
            maxTemperature: c.maxTemperature != null ? (typeof c.maxTemperature === 'object' ? c.maxTemperature.value : c.maxTemperature) : null,
            coatingNotes: c.coatingNotes || [],
            lastUpdatedUtc: c.lastUpdatedUtc,
        })),
        externalIds: (apiProduct.externalIds || []).map((e: any) => ({
            id: e.id,
            value: e.value,
            source: e.source,
            lastUpdatedUtc: e.lastUpdatedUtc,
        })),
        comments: (apiProduct.comments || []).map((c: any) => ({
            id: c.id,
            text: c.text,
            category: c.category,
            visibility: c.visibility,
            lastUpdatedUtc: c.lastUpdatedUtc,
        })),
        stowageRules: apiProduct.stowageRules || [],
        temperatureRequirements: apiProduct.temperatureRequirements,
        physicalData: apiProduct.physicalData,
        gasPhysicalData: apiProduct.gasPhysicalData,
        category: mapCategoryFromApi(apiProduct.category),
        tradeName: apiProduct.tradeName,
        mainHazards: apiProduct.mainHazards,
        charterer: apiProduct.charterer,
        regulations: mapProductRegulationsFromApi(apiProduct.regulations),
        materialSafetyDataSheet: apiProduct.materialSafetyDataSheet,
        materialSafetyDataSheets: (apiProduct.materialSafetyDataSheets || []).map((m: any) => ({
            id: m.id,
            lastUpdatedUtc: m.lastUpdatedUtc,
            comments: m.comments,
            filePath: m.filePath,
            receivedDate: m.receivedDate,
            receivedFrom: m.receivedFrom,
            date: m.date,
            version: m.version,
        })),
        productDocuments: (apiProduct.productDocuments || []).map((d: any) => ({
            id: d.id,
            lastUpdatedUtc: d.lastUpdatedUtc,
            filePath: d.filePath,
        })),
        draegerTubes: apiProduct.draegerTubes || [],
        isNonCargo: apiProduct.isNonCargo || false,
        isActive: apiProduct.isActive !== undefined ? apiProduct.isActive : true,
        isApproved: apiProduct.isApproved !== undefined ? apiProduct.isApproved : false,
        canUseInOrca: apiProduct.canUseInORCA || apiProduct.canUseInOrca,
        displayInformation: apiProduct.displayInformation,
    };
};

/**
 * Maps ProductRegulations object from API response
 */
const mapProductRegulationsFromApi = (apiRegulations: any): any => {
    if (!apiRegulations) return null;

    return {
        tripartiteAgreement: apiRegulations.tripartiteAgreement
            ? {
                id: apiRegulations.tripartiteAgreement.id,
                lastUpdatedUtc: apiRegulations.tripartiteAgreement.lastUpdatedUtc,
                reportingCountry: apiRegulations.tripartiteAgreement.reportingCountry,
                expiryDate: apiRegulations.tripartiteAgreement.expiryDate,
                countries: apiRegulations.tripartiteAgreement.countries,
                contains: apiRegulations.tripartiteAgreement.contains,
            }
            : null,
        uscgGroup: apiRegulations.uscgGroup
            ? {
                id: apiRegulations.uscgGroup.id,
                number: apiRegulations.uscgGroup.number,
                name: apiRegulations.uscgGroup.name,
                lastUpdatedUtc: apiRegulations.uscgGroup.lastUpdatedUtc,
            }
            : null,
        imdgCodeClass: apiRegulations.imdgCodeClass
            ? {
                id: apiRegulations.imdgCodeClass.id,
                classification: apiRegulations.imdgCodeClass.classification,
                description: apiRegulations.imdgCodeClass.description,
                lastUpdatedUtc: apiRegulations.imdgCodeClass.lastUpdatedUtc,
            }
            : null,
        persistentFloater: apiRegulations.persistentFloater,
        isOpa90: apiRegulations.isOpa90,
        numberOfLastCargoesForEpoxyTanksFosfaBan: apiRegulations.numberOfLastCargoesForEpoxyTanksFosfaBan,
        fosfaAllowance: apiRegulations.fosfaAllowance,
        euAllowance: apiRegulations.euAllowance,
        niop1Allowance: apiRegulations.niop1Allowance,
        niop2Allowance: apiRegulations.niop2Allowance,
    };
};

/**
 * Maps SafetyEquipment object from API response
 */
const mapSafetyEquipmentFromApi = (apiSafetyEquipment: any): any => {
    if (!apiSafetyEquipment) return null;

    return {
        id: apiSafetyEquipment.id,
        normalExposureLevel: apiSafetyEquipment.normalExposureLevel,
        highExposureLevel: apiSafetyEquipment.highExposureLevel,
        extremeExposureLevel: apiSafetyEquipment.extremeExposureLevel,
        safetyHazards: (apiSafetyEquipment.safetyHazards || []).map((h: any) => ({
            id: h.id,
            hazardCode: h.hazardCode,
            hazardDescription: h.hazardDescription,
            normalExposureLevel: h.normalExposureLevel,
            highExposureLevel: h.highExposureLevel,
            extremeExposureLevel: h.extremeExposureLevel,
            warningImage: h.warningImage,
            category: h.category,
            warningLevel: h.warningLevel,
            lastUpdatedUtc: h.lastUpdatedUtc,
            isActive: h.isActive,
            physicalPropertiesScore: h.physicalPropertiesScore,
            healthPropertiesScore: h.healthPropertiesScore,
            environmentPropertiesScore: h.environmentPropertiesScore,
            requiresExposureLog: h.requiresExposureLog,
        })),
        lastUpdatedUtc: apiSafetyEquipment.lastUpdatedUtc,
    };
};

/**
 * Maps Category Regulations object from API response (handles IBC/IGC nested structures)
 */
const mapCategoryRegulationsFromApi = (apiRegulations: any): any => {
    if (!apiRegulations) return null;

    const ibcRegulation = apiRegulations.ibcRegulation;
    const igcRegulation = apiRegulations.igcRegulation;

    return {
        marpolRegulation: apiRegulations.marpolRegulation,
        marpolPollutionCategory: apiRegulations.marpolPollutionCategory,
        ibcRegulation: ibcRegulation ? {
            id: ibcRegulation.id,
            hazards: ibcRegulation.hazards,
            pollutionCategory: ibcRegulation.pollutionCategory,
            shipType: ibcRegulation.shipType,
            tankType: ibcRegulation.tankType,
            tankVents: ibcRegulation.tankVents,
            tankEnvironmentalControl: ibcRegulation.tankEnvironmentalControl,
            electricalEquipment: ibcRegulation.electricalEquipment,
            gauging: ibcRegulation.gauging,
            vapourDetection: ibcRegulation.vapourDetection,
            fireProtection: ibcRegulation.fireProtection,
            cargoContainmentMaterialsOfConstruction: ibcRegulation.cargoContainmentMaterialsOfConstruction,
            emergencyEquipment: ibcRegulation.emergencyEquipment,
            requiresEmergencyEquipment: ibcRegulation.requiresEmergencyEquipment,
            specificAndOperationalRequirements: (ibcRegulation.specificAndOperationalRequirements || []).map((req: any) => ({
                id: req.id,
                paragraphNumber: req.paragraphNumber,
                paragraphHeading: req.paragraphHeading,
                text: req.text,
            })),
            lastUpdatedUtc: ibcRegulation.lastUpdatedUtc,
        } : null,
        igcRegulation: igcRegulation ? {
            id: igcRegulation.id,
            independentTankTypeCRequired: igcRegulation.independentTankTypeCRequired,
            specificAndOperationalRequirements: (igcRegulation.specificRequirements || igcRegulation.specificAndOperationalRequirements || []).map((req: any) => ({
                id: req.id,
                paragraphNumber: req.paragraphNumber,
                paragraphHeading: req.paragraphHeading,
                text: req.text,
            })),
            lastUpdatedUtc: igcRegulation.lastUpdatedUtc,
        } : null,
    };
};

/**
 * Maps Category object from API response
 */
const mapCategoryFromApi = (apiCategory: any): any => {
    if (!apiCategory) return null;

    return {
        id: apiCategory.id,
        name: apiCategory.name,
        isStaticAccumulator: apiCategory.isStaticAccumulator,
        thresholdLimitValueTimeWeightedAverage: apiCategory.thresholdLimitValueTimeWeightedAverage,
        odorThreshold: apiCategory.odorThreshold,
        externalIds: apiCategory.externalIds || [],
        synonyms: apiCategory.synonyms || [],
        comments: apiCategory.comments || [],
        draegerTubes: apiCategory.draegerTubes || [],
        stowageRules: apiCategory.stowageRules || [],
        regulations: mapCategoryRegulationsFromApi(apiCategory.regulations),
        physicalData: apiCategory.physicalData,
        safetyEquipment: mapSafetyEquipmentFromApi(apiCategory.safetyEquipment),
        lastUpdatedUtc: apiCategory.lastUpdatedUtc,
        isInhibited: apiCategory.isInhibited || false,
        shallNotBeExposedToExcessiveHeat: apiCategory.shallNotBeExposedToExcessiveHeat || false,
    };
};

/**
 * Maps list of products from API response
 */
export const mapProductsFromApi = (apiProducts: any[]): ProductDetail[] => {
    if (!Array.isArray(apiProducts)) {
        console.error('[ProductFactory] Expected array but got:', apiProducts);
        return [];
    }
    return apiProducts.map(mapProductFromApi);
};

/**
 * Converts ProductDetail to ProductListItem for list views
 */
export const mapProductToListItem = (product: ProductDetail): ProductListItem => {
    return {
        id: product.id,
        tradeName: product.tradeName || 'Unknown',
        categoryName: product.category?.name || 'Unknown',
        chartererId: product.charterer?.id,
        chartererName: product.charterer?.name,
        casNumber: product.externalIds?.find((e) => e.source?.toLowerCase() === 'cas')?.value,
        marpolRegulation: product.category?.regulations?.marpolRegulation?.toString(),
        isActive: product.isActive,
    };
};

/**
 * Converts list of ProductDetails to ProductListItems
 */
export const mapProductsToListItems = (products: ProductDetail[]): ProductListItem[] => {
    return products.map(mapProductToListItem);
};

/**
 * Maps QueryProductMetaData API response to ProductListItem array
 * Handles paginated response format from QueryProductMetaData endpoint
 */
export const mapQueryProductMetaDataResponse = (apiResponse: any): ProductListItem[] => {
    if (!apiResponse) {
        console.warn('[ProductFactory] No API response provided');
        return [];
    }

    // Handle paginated response format { items, totalCount, pageNumber, pageSize }
    if (apiResponse.items && Array.isArray(apiResponse.items)) {
        return apiResponse.items.map((item: any) => ({
            id: item.id || 0,
            tradeName: item.tradeName || null,
            categoryName: item.categoryName || 'Unknown',
            chartererId: item.chartererId ?? undefined,
            chartererName: item.chartererName,
            casNumber: item.casNumber,
            marpolRegulation: item.marpolRegulation,
            synonym: item.synonym,
            contains: item.contains || null,
            isActive: item.isActive !== undefined ? item.isActive : true,
        }));
    }

    // Handle direct array response (legacy format)
    if (Array.isArray(apiResponse)) {
        return apiResponse.map((item: any) => ({
            id: item.id || 0,
            tradeName: item.tradeName || null,
            categoryName: item.categoryName || 'Unknown',
            chartererId: item.chartererId ?? undefined,
            chartererName: item.chartererName,
            casNumber: item.casNumber,
            marpolRegulation: item.marpolRegulation,
            synonym: item.synonym,
            contains: item.contains || null,
            isActive: item.isActive !== undefined ? item.isActive : true,
        }));
    }

    console.error('[ProductFactory] Unexpected QueryProductMetaData response format:', apiResponse);
    return [];
};
